import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { MobileService } from "../services/mobile.service";
import { RegionService } from "../services/region.service";
import { UserService } from "./user.service";

/** Pages that open normally outside the allowed countries, so the notice never gets in their way. */
const EXEMPT_PATHS = ["/region-unavailable", "/reset-password", "/confirm-email"];
const DECIDED_KEY = "flipRegionEntry";

/**
 * FLIP-584. The first page a visitor lands on in a session, any page, sends them to /region-unavailable when
 * they are outside the countries flip* is sold in. From there they can choose "Sign in" and reach /login (where
 * "Create one" is hidden), so a travelling customer can still sign in.
 *
 * Decides once per tab session: the decision is remembered whether it allowed or redirected, so the Sign in
 * link, and a reload on /login, are not sent back to the notice. A new tab or session shows the notice again.
 * Never applied to: the native app (always allowed), anyone with a valid session (a travelling customer opens the
 * app as normal), and the account-recovery links from emails (/reset-password, /confirm-email) plus the notice
 * itself. Fails open like the rest of FLIP-584: RegionService treats an unknown country or a failed check as
 * allowed.
 *
 * Wired as `canActivateChild` on one empty-path parent route wrapping every route, so it sees the first
 * navigation to any URL before the page renders.
 */
@Injectable()
export class RegionEntryGuard implements CanActivateChild {
  /** Fallback for when sessionStorage throws (private windows, blocked site data). */
  private decidedInMemory = false;

  constructor(
    private router: Router,
    private mobileService: MobileService,
    private userService: UserService,
    private regionService: RegionService
  ) {}

  async canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (this.hasDecided()) {
      return true;
    }

    // This route is not behind AuthenticationGuard, so read the native shell's param ourselves, as
    // MobileOnlyGuard does, rather than trust MobileService to have seen it already.
    const appPlatform = childRoute.queryParams["appPlatform"];
    if (appPlatform) {
      this.mobileService.setPlatformType(appPlatform);
    }

    if (this.mobileService.isMobileApp || this.hasValidSession() || this.isExempt(state.url)) {
      this.markDecided();
      return true;
    }

    const allowed = await this.regionService.registrationAllowed();
    this.markDecided();
    return allowed ? true : this.router.parseUrl("/region-unavailable");
  }

  private hasValidSession(): boolean {
    return !!this.userService.getUser() && !this.userService.isTokenExpired();
  }

  private isExempt(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return EXEMPT_PATHS.some((p) => path === p || path.startsWith(p + "/"));
  }

  private hasDecided(): boolean {
    try {
      return this.decidedInMemory || sessionStorage.getItem(DECIDED_KEY) === "1";
    } catch {
      return this.decidedInMemory;
    }
  }

  private markDecided(): void {
    this.decidedInMemory = true;
    try {
      sessionStorage.setItem(DECIDED_KEY, "1");
    } catch {
      // Storage unavailable: the in-memory flag covers the rest of this page's life.
    }
  }
}
